/*
 * @Author: huangxuejuan
 * @Date: 2026-09-01 10:00:00
 * @Description: 自定义卡片统一维护（新增卡片：本文件加 route key + 组件映射，再新建卡片组件即可）：
 * content_metadata 内层 ai_agent_card_route → CARD_ROUTE → CARD_COMPONENT_MAP 映射组件渲染
 */
import type { Component } from "vue"
import type { CardPayload, ContentMetadataItem, MessageSegment } from "@/types/app"
import MonthlyReportCard from "./MonthlyReportCard.vue"
import PremiumWelcomeCard from "./PremiumWelcomeCard.vue"

// 本期支持的卡片路由 key（content_metadata 内层 ai_agent_card_route 字段值，实际下发为后台工具名）
export const CARD_ROUTE = {
  MONTHLY_REPORT: "query_mpos_month_report_mcp", // 经营月报卡片
  PREMIUM_WELCOME: "premium_welcome" // 优享客户欢迎语卡片（本地插入，后台下发时兜底渲染）
} as const

// 卡片相对文案的展示位置（content_metadata 内层 ai_agent_card_mode 字段，docs/报文1/2.txt）
export const CARD_MODE = {
  AFTER_CARD: "after_card", // 卡片在文案之前（卡片在开头）
  AFTER_CONTENT: "after_content" // 卡片在文案之后（卡片在结尾）
} as const

// route → 卡片组件映射（未映射的 route 在 parseCardMetadata 中过滤，不会进入渲染）
export const CARD_COMPONENT_MAP: Record<string, Component> = {
  [CARD_ROUTE.MONTHLY_REPORT]: MonthlyReportCard,
  [CARD_ROUTE.PREMIUM_WELCOME]: PremiumWelcomeCard
}

// 自带独立气泡描边、不进白底消息框混排的卡片 route（如优享欢迎语的渐变描边气泡）
export const STANDALONE_CARD_ROUTES = new Set<string>([CARD_ROUTE.PREMIUM_WELCOME])

/**
 * 解析 SSE content_metadata 中的自定义卡片数据
 * 兼容两种工具结果格式（后台两套链路下发不一致）：
 * ① 双重 JSON 字符串：result 为 "[{\"text\": \"{...卡片数据...}\"}]" 字符串，text 再反序列化
 * ② 结构化对象：result 直接为 [{text: {…卡片数据…}}] 对象数组（docs/报文.txt 实测）
 * 未知 route / 解析失败一律忽略（返回空数组），不影响文本流渲染
 */
export function parseCardMetadata(metadataList?: ContentMetadataItem[] | null): CardPayload[] {
  if (!Array.isArray(metadataList)) return []

  // 归一化：string 时反序列化，object 直接使用
  const normalizeToList = (raw: unknown): unknown[] => {
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
      } catch {
        return []
      }
    }
    return Array.isArray(raw) ? raw : []
  }

  const cards: CardPayload[] = []
  for (const metadata of metadataList) {
    const rawResult = metadata?.response?.result
    if (!rawResult) continue

    for (const item of normalizeToList(rawResult)) {
      const text = (item as { text?: unknown })?.text
      if (!text) continue

      // text 归一化：JSON 字符串需再反序列化，对象直接使用
      let cardData: Record<string, any> | null = null
      if (typeof text === "string") {
        try {
          const parsed = JSON.parse(text)
          if (parsed && typeof parsed === "object") cardData = parsed
        } catch {
          // text 非合法 JSON，忽略
        }
      } else if (typeof text === "object") {
        cardData = text as Record<string, any>
      }
      if (!cardData) continue

      const route = cardData.ai_agent_card_route
      if (route && CARD_COMPONENT_MAP[route]) {
        // 透传工具入参（月报链路 stat_month 用于卡片标题/截至日期），text 内已带同名字段时不覆盖
        const params = metadata?.request?.params
        if (params && cardData.request_params === undefined) {
          cardData.request_params = params
        }
        cards.push({ route, data: cardData })
      }
    }
  }
  return cards
}

/**
 * 按 ai_agent_card_mode 排序分段（卡片+文案组合消息的 4 种场景：只有文案/只有卡片/卡片在开头/卡片在结尾）：
 * - after_card（卡片在开头）：卡片段排到所有文案段之前
 * - after_content（卡片在结尾）：卡片段排到所有文案段之后
 * - 未声明 mode：保持传入顺序（流式即到达顺序，历史反显即构建顺序文案在前）
 * 流式与历史反显共用（AgentChatAnswer 渲染前统一归一化，后端元数据到达时机与 mode 不一致时展示仍正确）
 */
export function orderSegmentsByCardMode(segments: MessageSegment[]): MessageSegment[] {
  if (segments.length < 2) return segments
  const cardSegs = segments.filter((seg): seg is Extract<MessageSegment, { type: "card" }> => seg.type === "card")
  const textSegs = segments.filter((seg): seg is Extract<MessageSegment, { type: "text" }> => seg.type === "text")
  if (!cardSegs.length || !textSegs.length) return segments
  const mode = cardSegs.find((seg) => seg.card.data?.ai_agent_card_mode)?.card.data?.ai_agent_card_mode
  if (mode === CARD_MODE.AFTER_CARD) return [...cardSegs, ...textSegs]
  if (mode === CARD_MODE.AFTER_CONTENT) return [...textSegs, ...cardSegs]
  return segments
}
